import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/apiConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  UserCheck,
  AlertCircle,
  Eye,
  EyeOff,
  Shield,
  Save,
  X,
  ArrowLeftRight,
  Wallet,
  Send,
  Link,
  ShieldCheck,
  Code,
  Settings,
} from "lucide-react";
import Permission from "../components/Permission";
import usePermissions from "../hooks/usePermissions";
import ProtectedRoute from "../components/ProtectedRoute";
import { useTranslation } from "react-i18next";
import { PERMISSION_DEFINITIONS } from "../utils/rbacConfig";

// Icon map for permission grid
const PERMISSION_ICONS = {
  ArrowLeftRight,
  Wallet,
  Send,
  Link,
  Wallet2: Wallet,
  ShieldCheck,
  Users,
  Code,
  Settings,
};

const RoleManagementPage = () => {
  const navigate = useNavigate();
  const { canPerformAction, userRole, hasPermission } = usePermissions();
  const [subUsers, setSubUsers] = useState([]);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDelete2FAModal, setShowDelete2FAModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    permissions: [],
  });
  const [passwordData, setPasswordData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [twoFAData, setTwoFAData] = useState({
    enabled2FA: "false",
    type2FA: null,
    verified2FA: "false",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordChanging, setIsPasswordChanging] = useState(false);
  const [is2FAToggling, setIs2FAToggling] = useState(false);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Toast helpers
  const showSuccessToast = (message) => {
    toast.success(message, { position: "top-right", autoClose: 2000 });
  };
  const showErrorToast = (message) => {
    toast.error(message, { position: "top-right", autoClose: 2000 });
  };

  // ── Permission checkbox helpers ──────────────────────────────────────────
  const togglePermission = (key) => {
    setFormData((prev) => {
      const perms = prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key];
      return { ...prev, permissions: perms };
    });
  };

  const selectAllPermissions = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: PERMISSION_DEFINITIONS.map((d) => d.key),
    }));
  };

  const deselectAllPermissions = () => {
    setFormData((prev) => ({ ...prev, permissions: [] }));
  };

  const allSelected = formData.permissions.length === PERMISSION_DEFINITIONS.length;

  // ── Fetch sub-users ──────────────────────────────────────────────────────
  const fetchSubUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/subusers`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.success) {
        setSubUsers(data.data || []);
      } else {
        setSubUsers([]);
      }
    } catch (err) {
      setSubUsers([]);
      showErrorToast(err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubUsers();
  }, []);

  // ── Password change ──────────────────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsPasswordChanging(true);
    try {
      if (passwordData.password !== passwordData.confirmPassword) {
        showErrorToast("Passwords do not match!");
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/subusers/${selectedUser._id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: passwordData.password }),
        }
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to update password");
      }

      setShowPasswordModal(false);
      setPasswordData({ password: "", confirmPassword: "" });
      setError(null);
      showSuccessToast("Password updated successfully!");
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setIsPasswordChanging(false);
    }
  };

  // ── 2FA ──────────────────────────────────────────────────────────────────
  const fetch2FAStatus = async (subUserId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/subusers/${subUserId}/2fa-status`,
        { credentials: "include", headers: { "Content-Type": "application/json" } }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setTwoFAData({
          enabled2FA: data.data.enabled2FA,
          type2FA: data.data.type2FA,
          verified2FA: data.data.verified2FA,
        });
      } else {
        setTwoFAData({ enabled2FA: "false", type2FA: null, verified2FA: "false" });
      }
    } catch (err) {
      console.error("Failed to fetch 2FA status:", err);
      setTwoFAData({ enabled2FA: "false", type2FA: null, verified2FA: "false" });
    }
  };

  const handle2FAToggle = async (enable, type2FA = null) => {
    setIs2FAToggling(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/subusers/${selectedUser._id}/toggle-2fa`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enable: enable.toString(), type2FA }),
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        showSuccessToast(data.message);
        setTwoFAData({
          enabled2FA: enable ? "true" : "false",
          type2FA: enable ? type2FA : null,
          verified2FA: "false",
        });
      } else {
        throw new Error(data.message || "Failed to toggle 2FA");
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setIs2FAToggling(false);
    }
  };

  // ── Create / Edit sub-user ───────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!showEditModal) {
        if (formData.password !== formData.confirmPassword) {
          showErrorToast("Passwords do not match!");
          return;
        }
        if (formData.password.length < 6) {
          showErrorToast("Password must be at least 6 characters long");
          return;
        }
      }

      // Validate at least one permission
      if (formData.permissions.length === 0) {
        showErrorToast("Please select at least one permission");
        return;
      }

      const url = showEditModal
        ? `${API_BASE_URL}/subusers/${selectedUser._id}`
        : `${API_BASE_URL}/subusers`;
      const method = showEditModal ? "PUT" : "POST";

      const submitData = {
        name: formData.name,
        email: formData.email,
        permissions: formData.permissions,
        role: "team_member",
      };

      if (!showEditModal) {
        submitData.password = formData.password;
      }

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submitData),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to save sub-user");
      }

      fetchSubUsers();
      resetForm();
      showSuccessToast(
        showEditModal ? "User updated successfully!" : "User created successfully!"
      );
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Status change ────────────────────────────────────────────────────────
  const handleStatusChange = async (userId, newStatus) => {
    setIsStatusChanging(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subusers/${userId}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update status");
      if (data.success) {
        fetchSubUsers();
        showSuccessToast("Status updated successfully!");
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setIsStatusChanging(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subusers/${selectedUser._id}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to delete sub-user");
      if (data.success) {
        fetchSubUsers();
        showSuccessToast("User deleted successfully!");
        setShowDeleteModal(false);
        setSelectedUser(null);
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Delete 2FA ───────────────────────────────────────────────────────────
  const handleDelete2FA = (user) => {
    setSelectedUser(user);
    setShowDelete2FAModal(true);
  };

  const confirmDelete2FA = async () => {
    if (!selectedUser) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subusers/2fa/${selectedUser._id}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to delete 2FA");
      if (data.success) {
        fetchSubUsers();
        showSuccessToast("2FA deleted successfully!");
        setShowDelete2FAModal(false);
        setSelectedUser(null);
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
      permissions: [],
    });
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedUser(null);
  };

  // ── Helper: count new-format permissions on a user ────────────────────────
  const getPermissionCount = (user) => {
    if (!user.permissions || !Array.isArray(user.permissions)) return 0;
    return user.permissions.filter((p) =>
      PERMISSION_DEFINITIONS.some((d) => d.key === p)
    ).length;
  };

  const getPermissionLabels = (user) => {
    if (!user.permissions || !Array.isArray(user.permissions)) return [];
    return PERMISSION_DEFINITIONS
      .filter((d) => user.permissions.includes(d.key))
      .map((d) => d.label);
  };

  // ── Permission check ─────────────────────────────────────────────────────
  if (!hasPermission("manage_team")) {
    return (
      <ProtectedRoute page="/role-management">
        <div></div>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); fetchSubUsers(); }}
            className="bg-red-500 text-white px-6 py-3 rounded hover:bg-red-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="w-full">
        <ToastContainer position="top-right" autoClose={3000} />

        {/* Header */}
        <div className="mb-6 px-4 md:px-0">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t("roleManagement.title")}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {t("roleManagement.subtitle")}
              </p>
            </div>
            <Permission permission="manage_team">
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                  borderRadius: "10px",
                }}
                className="flex items-center px-3 py-2 sm:px-4 text-white rounded-lg hover:opacity-90 transition-colors text-sm"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                <span className="hidden sm:inline">{t("roleManagement.addSubUser")}</span>
                <span className="sm:hidden">{t("roleManagement.create")}</span>
              </button>
            </Permission>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-[25px] shadow-sm overflow-hidden">
          <div className="overflow-x-auto p-2">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium whitespace-nowrap text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("roleManagement.srNo")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("roleManagement.name")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("roleManagement.email")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("permissions.access") || "Access"}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("roleManagement.status")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("roleManagement.date")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 capitalize" style={{ fontSize: "14px" }}>
                    {t("roleManagement.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  [...Array(6)].map((_, index) => (
                    <tr key={`skeleton-${index}`} className="animate-pulse">
                      <td className="px-4 py-2 whitespace-nowrap"><span className="inline-block h-4 bg-gray-200 rounded w-8"></span></td>
                      <td className="px-4 py-2 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                      <td className="px-4 py-2 whitespace-nowrap"><span className="inline-block h-4 bg-gray-200 rounded w-40"></span></td>
                      <td className="px-4 py-2 whitespace-nowrap"><span className="inline-block h-6 bg-gray-200 rounded-full w-24"></span></td>
                      <td className="px-4 py-2 whitespace-nowrap"><span className="inline-block h-7 bg-gray-200 rounded-[25px] w-20"></span></td>
                      <td className="px-4 py-2 whitespace-nowrap"><span className="inline-block h-4 bg-gray-200 rounded w-28"></span></td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <span className="inline-block h-8 w-8 bg-gray-200 rounded-lg"></span>
                          <span className="inline-block h-8 w-8 bg-gray-200 rounded-lg"></span>
                          <span className="inline-block h-8 w-8 bg-gray-200 rounded-lg"></span>
                          <span className="inline-block h-8 w-8 bg-gray-200 rounded-lg"></span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : subUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium">{t("roleManagement.noRolesFound")}</p>
                        <p className="text-sm text-gray-400 mt-1">{t("roleManagement.noRolesMessage")}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  subUsers.map((user, index) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {getPermissionCount(user) > 0 ? (
                          <div className="group relative">
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full cursor-help">
                              {getPermissionCount(user)} of {PERMISSION_DEFINITIONS.length}
                            </span>
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50">
                              <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg max-w-xs">
                                {getPermissionLabels(user).join(", ")}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200 rounded-full">
                            {user.role || "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <select
                          value={user.status}
                          onChange={(e) => handleStatusChange(user._id, e.target.value)}
                          className={`rounded-[25px] text-center px-2 pr-3 py-1 text-sm appearance-none bg-no-repeat bg-right ${
                            user.status === "active"
                              ? "bg-green-100 text-green-700"
                              : user.status === "inactive"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                          }`}
                          style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='black' viewBox='0 0 24 24' width='16' height='16'><path d='M7 10l5 5 5-5z'/></svg>")` }}
                        >
                          <option value="active">{t("roleManagement.active")}</option>
                          <option value="inactive">{t("roleManagement.inactive")}</option>
                          <option value="blocked">{t("roleManagement.blocked")}</option>
                        </select>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
                            })
                          : "N/A"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <Permission permission="manage_team">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setFormData({
                                  name: user.name || "",
                                  email: user.email || "",
                                  password: "",
                                  confirmPassword: "",
                                  permissions: user.permissions || [],
                                });
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 bg-blue-50 p-2 rounded-lg transition-colors"
                              title="Edit User"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </Permission>
                          <Permission permission="manage_team">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setPasswordData({ password: "", confirmPassword: "" });
                                setShowPasswordModal(true);
                              }}
                              className="text-green-600 hover:text-green-800 bg-green-50 p-2 rounded-lg transition-colors"
                              title="Change Password"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                          </Permission>
                          <Permission permission="manage_team">
                            <button
                              disabled={!(user.enabled2FA && user.enabled2FA === "true")}
                              onClick={() => handleDelete2FA(user)}
                              className={`text-purple-600 hover:text-purple-800 bg-purple-50 p-2 rounded-lg transition-colors ${
                                !(user.enabled2FA && user.enabled2FA === "true") ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              title="Delete 2FA"
                            >
                              <Shield className="w-4 h-4" />
                            </button>
                          </Permission>
                          <Permission permission="manage_team">
                            <button
                              onClick={() => handleDelete(user)}
                              className="text-red-600 hover:text-red-800 bg-red-50 p-2 rounded-lg transition-colors"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Permission>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Add/Edit Modal with Permission Checkboxes ───────────────────── */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[15px] max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                {showEditModal ? (
                  <><Edit2 className="w-5 h-5" /> {t("roleManagement.editSubUser")}</>
                ) : (
                  <><Plus className="w-5 h-5" /> {t("roleManagement.addNewSubUser")}</>
                )}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("roleManagement.fullName")} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder={t("roleManagement.fullNamePlaceholder")}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("roleManagement.emailAddress")} *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder={t("roleManagement.emailPlaceholder")}
                />
              </div>

              {/* Password (create only) */}
              {!showEditModal && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("roleManagement.password")} *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                        required
                        placeholder={t("roleManagement.passwordPlaceholder")}
                        minLength={6}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("roleManagement.confirmPassword")} *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                          formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword
                            ? "border-red-300" : "border-gray-300"
                        }`}
                        required
                        placeholder={t("roleManagement.confirmPasswordPlaceholder")}
                        minLength={6}
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {t("roleManagement.passwordsDoNotMatch")}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Permission Checkboxes Grid ─────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("permissions.title") || "Permissions"} *
                  </label>
                  <button
                    type="button"
                    onClick={allSelected ? deselectAllPermissions : selectAllPermissions}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {allSelected
                      ? (t("permissions.deselectAll") || "Deselect All")
                      : (t("permissions.selectAll") || "Select All")}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PERMISSION_DEFINITIONS.map((def) => {
                    const IconComp = PERMISSION_ICONS[def.icon] || Shield;
                    const isChecked = formData.permissions.includes(def.key);
                    const isMoney = def.isMoneySensitive;

                    return (
                      <label
                        key={def.key}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isChecked
                            ? isMoney
                              ? "bg-amber-50 border-amber-300"
                              : "bg-blue-50 border-blue-300"
                            : "bg-white border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePermission(def.key)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <IconComp className={`w-4 h-4 flex-shrink-0 ${isChecked ? (isMoney ? "text-amber-600" : "text-blue-600") : "text-gray-400"}`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 flex items-center gap-1">
                            {t(`permissions.${def.key}`) || def.label}
                            {isMoney && (
                              <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {t(`permissions.${def.key}_desc`) || def.description}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {formData.permissions.length === 0 && (
                  <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {t("permissions.selectAtLeastOne") || "Select at least one permission"}
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)", borderRadius: "10px" }}
                  className="flex-1 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {showEditModal ? t("roleManagement.updating") : t("roleManagement.creating")}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {showEditModal ? t("roleManagement.updateUser") : t("roleManagement.createUser")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Password Change Modal ───────────────────────────────────────── */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[15px] max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                {t("roleManagement.changePasswordTitle")}
              </h2>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                {t("roleManagement.updatingPasswordFor")}: <strong>{selectedUser?.name}</strong>
              </p>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("roleManagement.newPassword")} *</label>
                <input
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required placeholder={t("roleManagement.newPasswordPlaceholder")} minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("roleManagement.confirmNewPassword")} *</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${passwordData.password && passwordData.confirmPassword && passwordData.password !== passwordData.confirmPassword ? "border-red-300" : "border-gray-300"}`}
                  required placeholder={t("roleManagement.confirmNewPasswordPlaceholder")} minLength={6}
                />
                {passwordData.password && passwordData.confirmPassword && passwordData.password !== passwordData.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{t("roleManagement.passwordsDoNotMatch")}</p>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 flex items-center justify-center gap-2">
                  <X className="w-4 h-4" />{t("common.cancel")}
                </button>
                <button type="submit" disabled={isPasswordChanging} style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)", borderRadius: "10px" }}
                  className="flex-1 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isPasswordChanging ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t("roleManagement.updating")}</>)
                    : (<><Save className="w-4 h-4" />{t("roleManagement.updatePassword")}</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── 2FA Management Modal ────────────────────────────────────────── */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {t("roleManagement.twoFAEnabled")} - {selectedUser?.name}
              </h3>
              <button onClick={() => setShow2FAModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">{t("roleManagement.currentStatus")}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t("roleManagement.twoFAEnabled")}:</span>
                    <span className={`font-medium ${twoFAData.enabled2FA === "true" ? "text-green-600" : "text-red-600"}`}>
                      {twoFAData.enabled2FA === "true" ? t("roleManagement.yes") : t("roleManagement.no")}
                    </span>
                  </div>
                  {twoFAData.enabled2FA === "true" && (
                    <>
                      <div className="flex justify-between">
                        <span>{t("roleManagement.type")}:</span>
                        <span className="font-medium">{twoFAData.type2FA?.replace("_", " ")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("roleManagement.verified")}:</span>
                        <span className={`font-medium ${twoFAData.verified2FA === "true" ? "text-green-600" : "text-orange-600"}`}>
                          {twoFAData.verified2FA === "true" ? t("roleManagement.yes") : t("roleManagement.pending")}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {twoFAData.enabled2FA === "false" ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700">{t("roleManagement.enable2FA")}</h4>
                  <div className="grid gap-2">
                    <button onClick={() => handle2FAToggle(true, "EMAIL")} disabled={is2FAToggling}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {is2FAToggling ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t("roleManagement.enabling")}</>)
                        : t("roleManagement.enableEmail2FA")}
                    </button>
                    <button onClick={() => handle2FAToggle(true, "GOOGLE_AUTH")} disabled={is2FAToggling}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {is2FAToggling ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t("roleManagement.enabling")}</>)
                        : t("roleManagement.enableGoogleAuth")}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => handle2FAToggle(false)} disabled={is2FAToggling}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {is2FAToggling ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t("roleManagement.disabling")}</>)
                    : (<><X className="w-4 h-4" />{t("roleManagement.disable2FA")}</>)}
                </button>
              )}
              <div className="pt-4 border-t">
                <button onClick={() => setShow2FAModal(false)} className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ───────────────────────────────────── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[15px] max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><Trash2 className="w-6 h-6 text-red-600" />{t("roleManagement.deleteUserTitle")}</h2>
              <button onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center"><AlertCircle className="w-5 h-5 text-red-600 mr-2" /><span className="text-red-800 font-medium">{t("roleManagement.warning")}</span></div>
                <p className="text-red-700 text-sm mt-2">{t("roleManagement.deleteWarningMessage")}</p>
              </div>
              <p className="text-gray-600">{t("roleManagement.deleteConfirmation")} <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }} className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600">{t("common.cancel")}</button>
              <button onClick={confirmDelete} disabled={isDeleting} className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {isDeleting ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t("roleManagement.deleting")}</>)
                  : (<><Trash2 className="w-4 h-4" />{t("roleManagement.deleteUserTitle")}</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete 2FA Confirmation Modal ───────────────────────────────── */}
      {showDelete2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><Shield className="w-6 h-6 text-purple-600" />{t("roleManagement.delete2FATitle")}</h2>
              <button onClick={() => { setShowDelete2FAModal(false); setSelectedUser(null); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="mb-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <div className="flex items-center"><AlertCircle className="w-5 h-5 text-purple-600 mr-2" /><span className="text-purple-800 font-medium">{t("roleManagement.securityNotice")}</span></div>
                <p className="text-purple-700 text-sm mt-2">{t("roleManagement.delete2FAMessage")}</p>
              </div>
              <p className="text-gray-600">{t("roleManagement.delete2FAConfirmation")} <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowDelete2FAModal(false); setSelectedUser(null); }} className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600">{t("common.cancel")}</button>
              <button onClick={confirmDelete2FA} disabled={isDeleting} className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {isDeleting ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t("roleManagement.deleting")}</>)
                  : (<><Shield className="w-4 h-4" />{t("roleManagement.delete2FATitle")}</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagementPage;
